import { type UserRole } from "../types";

/**
 * Role types supported by the application
 */
export type Role = "admin" | "operator" | "client" | "user";

/**
 * JWT Token payload interface
 */
export interface TokenPayload {
  sub?: string; // subject (user ID)
  email?: string;
  roles?: string[];
  permissions?: string[]; // Changed to string array to match backend format
  iat?: number; // issued at
  exp?: number; // expiration
  [key: string]: unknown; // allow additional claims
}

/**
 * Extract permissions from JWT token payload
 * @param tokenPayload - The decoded JWT payload
 * @returns Array of user permissions
 */
export function getUserPermissions(tokenPayload: TokenPayload): string[] {
  if (!tokenPayload || !tokenPayload.permissions) {
    return [];
  }

  return tokenPayload.permissions;
}

/**
 * Check if user has required role(s)
 * @param required - Single role or array of roles to check
 * @param userRoles - Array of user's roles
 * @returns true if user has any of the required roles
 */
export function hasRole(required: Role | Role[], userRoles: Role[]): boolean {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }

  const requiredRoles = Array.isArray(required) ? required : [required];

  // Check if user has any of the required roles
  return requiredRoles.some((role) => userRoles.includes(role));
}

/**
 * Check if user has all required roles
 * @param required - Array of roles that user must have
 * @param userRoles - Array of user's roles
 * @returns true if user has all required roles
 */
export function hasAllRoles(required: Role[], userRoles: Role[]): boolean {
  if (!userRoles || !required || required.length === 0) {
    return false;
  }

  return required.every((role) => userRoles.includes(role));
}

/**
 * Check if user has required permission
 * @param requiredPermission - Permission to check (format: "Resource:Action")
 * @param userPermissions - Array of user's permissions
 * @returns true if user has the required permission
 */
export function hasPermission(
  requiredPermission: string,
  userPermissions: string[],
): boolean {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }

  return userPermissions.includes(requiredPermission);
}

/**
 * Check if user has any of the required permissions
 * @param requiredPermissions - Array of permissions to check
 * @param userPermissions - Array of user's permissions
 * @returns true if user has any of the required permissions
 */
export function hasAnyPermission(
  requiredPermissions: string[],
  userPermissions: string[],
): boolean {
  if (
    !userPermissions ||
    !requiredPermissions ||
    requiredPermissions.length === 0
  ) {
    return false;
  }

  return requiredPermissions.some((permission) =>
    userPermissions.includes(permission),
  );
}

/**
 * Check if user has all required permissions
 * @param requiredPermissions - Array of permissions that user must have
 * @param userPermissions - Array of user's permissions
 * @returns true if user has all required permissions
 */
export function hasAllPermissions(
  requiredPermissions: string[],
  userPermissions: string[],
): boolean {
  if (
    !userPermissions ||
    !requiredPermissions ||
    requiredPermissions.length === 0
  ) {
    return false;
  }

  return requiredPermissions.every((permission) =>
    userPermissions.includes(permission),
  );
}

/**
 * Extract roles from User object (for compatibility with existing AuthContext)
 * @param userRoles - Array of UserRole objects from User
 * @returns Array of role names as Role types
 */
export function extractRolesFromUser(userRoles: UserRole[]): Role[] {
  if (!userRoles || userRoles.length === 0) {
    return [];
  }

  const validRoles: Role[] = ["admin", "operator", "client", "user"];
  return userRoles
    .map((role) => role.name)
    .filter((role): role is Role => validRoles.includes(role as Role));
}

/**
 * Extract user roles from JWT token payload
 * @param tokenPayload - The decoded JWT payload
 * @returns Array of user roles
 */
export function getUserRoles(tokenPayload: TokenPayload): Role[] {
  if (!tokenPayload || !tokenPayload.roles) {
    return [];
  }

  // Filter roles to only include valid Role types
  const validRoles: Role[] = ["admin", "operator", "client", "user"];
  return tokenPayload.roles.filter((role): role is Role =>
    validRoles.includes(role as Role),
  );
}

/**
 * Parse JWT token payload (basic implementation)
 * Note: In production, use a proper JWT library like 'jsonwebtoken' or 'jose'
 * @param token - JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function parseTokenPayload(token: string): TokenPayload | null {
  try {
    // Basic JWT parsing (header.payload.signature)
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (middle part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded) as TokenPayload;
  } catch (error) {
    console.warn("Failed to parse token payload:", error);
    return null;
  }
}

/**
 * Check if token is expired
 * @param tokenPayload - The decoded JWT payload
 * @returns true if token is expired
 */
export function isTokenExpired(tokenPayload: TokenPayload): boolean {
  if (!tokenPayload.exp) {
    return false; // No expiration claim
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return tokenPayload.exp < currentTime;
}

/**
 * Get user permissions from token string
 * @param token - JWT token string
 * @returns Array of user permissions or empty array if invalid/expired
 */
export function getUserPermissionsFromToken(token: string): string[] {
  const payload = parseTokenPayload(token);
  if (!payload || isTokenExpired(payload)) {
    return [];
  }

  return getUserPermissions(payload);
}

/**
 * Get user roles from token string
 * @param token - JWT token string
 * @returns Array of user roles or empty array if invalid/expired
 */
export function getUserRolesFromToken(token: string): Role[] {
  const payload = parseTokenPayload(token);
  if (!payload || isTokenExpired(payload)) {
    return [];
  }

  return getUserRoles(payload);
}
